import { SetStateAction, useEffect, useRef, useState } from 'react';
import { BookingApiError, createReservation, loadReservationStatus, parseCredentials, parseReservationPayload } from './api';
import {
  BookingQuote, BookingSelection, Customer, ReservationAttempt, ReservationCredentials, ReservationStatus,
  canOpenPayment, canStartNewReservation, isFinalReservationStatus, isUsableQuote, validateCustomer,
} from './model';
import { ReservationSession, readReservationSession, saveReservationSession, clearReservationSession } from './reservationSession';

interface Props {
  selection: BookingSelection | null;
  quote: BookingQuote | null;
  termsVersion: string | null;
  canContinue: boolean;
  message: string;
  locale: 'pl' | 'en' | 'de';
  paymentReturnId?: string | null;
  onLeavePaymentReturn?: () => Promise<boolean>;
  onConflict: (code: string) => void;
}

const conflicts = new Set(['PRICE_CHANGED', 'QUOTE_EXPIRED', 'BOOKING_CONFLICT', 'ACCESSORY_UNAVAILABLE', 'TERMS_CHANGED']);
const beforeWrite = new Set([...Array.from(conflicts), 'ACCESS_DENIED', 'CONFIG_UNAVAILABLE', 'VALIDATION_ERROR',
  'INVALID_RANGE', 'OUTSIDE_HOURS', 'INVALID_LOCAL_TIME', 'OFFER_DISABLED', 'INVALID_SELECTION', 'INVALID_PAYMENT_TYPE']);

export function useReservation(props: Props) {
  const [customer, setCustomer] = useState<Customer>({ name: '', email: '', phone: '' });
  const [consents, updateConsents] = useState({ terms: false, privacy: false, deposit: false, marketing: false });
  const [confirmedTermsVersion, setConfirmedTermsVersion] = useState('');
  const [acceptedQuoteId, setAcceptedQuoteId] = useState('');
  const [attempt, setAttempt] = useState<ReservationAttempt | null>(null);
  const [credentials, setCredentials] = useState<ReservationCredentials | null>(null);
  const [status, setStatus] = useState<ReservationStatus | null>(null);
  const [errorCode, setErrorCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [recoveryBlocked, setRecoveryBlocked] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [recoveredPayload, setRecoveredPayload] = useState<ReservationAttempt['payload'] | null>(null);
  const [statusRevision, setStatusRevision] = useState(0);
  const [retryAt, setRetryAt] = useState(0);
  const [clock, setClock] = useState(0);
  const busy = useRef(false);
  const mounted = useRef(true);
  const createController = useRef<AbortController | null>(null);
  const saved = useRef<ReservationSession>({ savedAt: 0 });
  const automaticRedirect = useRef(false);
  const redirected = useRef(false);

  useEffect(() => {
    mounted.current = true;
    setClock(Date.now());
    const tick = setInterval(() => setClock(Date.now()), 1000);
    try {
      const session = readReservationSession(window.sessionStorage);
      if (session) {
        saved.current = session;
        setCredentials(session.credentials ?? null); setAttempt(session.attempt ?? null);
        setRecoveryBlocked(!!session.unresolved);
        if (session.attempt) {
          setRecoveredPayload(session.attempt.payload); setCustomer(session.attempt.payload.customer);
        }
      }
    } catch (error) {
      setErrorCode(error instanceof BookingApiError ? error.code : 'STORAGE_UNAVAILABLE');
      setRecoveryBlocked(true);
    } finally { setSessionReady(true); }
    return () => { mounted.current = false; clearInterval(tick); createController.current?.abort(); };
  }, []);

  useEffect(() => {
    setAcceptedQuoteId('');
  }, [props.quote?.quoteId]);
  useEffect(() => {
    updateConsents(previous => ({ ...previous, terms: false, privacy: false, deposit: false }));
    setConfirmedTermsVersion('');
  }, [props.termsVersion]);
  const setConsents = (value: SetStateAction<typeof consents>) => {
    updateConsents(previous => {
      const current = confirmedTermsVersion === props.termsVersion ? previous
        : { ...previous, terms: false, privacy: false, deposit: false };
      return typeof value === 'function' ? value(current) : value;
    });
    setConfirmedTermsVersion(props.termsVersion ?? '');
  };

  const mismatch = props.paymentReturnId !== undefined
    && (!credentials || (props.paymentReturnId !== null && props.paymentReturnId !== credentials.reservationId));
  const persist = (session: ReservationSession) => {
    saveReservationSession(window.sessionStorage, session);
    saved.current = session;
  };
  useEffect(() => {
    const session = saved.current;
    if (session.attempt && clock - session.attempt.createdAt > 60 * 60_000) {
      try {
        const next = { ...session, attempt: undefined, unresolved: !session.credentials };
        persist(next); setAttempt(null); setRecoveryBlocked(!!next.unresolved);
      } catch { setErrorCode('STORAGE_UNAVAILABLE'); setRecoveryBlocked(true); }
    }
    if (session.credentials && clock - session.savedAt > 24 * 60 * 60_000) {
      try {
        persist({ savedAt: session.savedAt, unresolved: true });
        setAttempt(null); setCredentials(null); setRecoveryBlocked(true);
      } catch { setErrorCode('STORAGE_UNAVAILABLE'); setRecoveryBlocked(true); }
    }
    // Prune private recovery data even when the tab stays open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clock]);
  const remember = (value: ReservationCredentials) => {
    setCredentials(value);
    saved.current = { ...saved.current, credentials: value };
    persist(saved.current);
  };
  const handleStatus = (value: ReservationStatus) => {
    setStatus(value);
    if (value.paymentState === 'ready' || isFinalReservationStatus(value)) {
      persist({ ...saved.current, attempt: undefined });
      setAttempt(null);
    }
    if (automaticRedirect.current && props.paymentReturnId === undefined && canOpenPayment(value) && !redirected.current) {
      persist(saved.current);
      redirected.current = true;
      window.location.assign(value.redirectUri!);
    }
  };

  useEffect(() => {
    if (!credentials || mismatch || recoveryBlocked) return;
    let active = true, inFlight = false, failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const deadline = Date.now() + 60_000;
    let nextAllowed = retryAt;
    const poll = async () => {
      if (!active || inFlight || document.hidden) return;
      if (Date.now() >= deadline) { setPolling(false); return; }
      if (Date.now() < nextAllowed) { timer = setTimeout(poll, Math.min(nextAllowed - Date.now(), deadline - Date.now())); return; }
      inFlight = true; controller = new AbortController(); setPolling(true);
      try {
        const result = await loadReservationStatus(credentials, controller.signal);
        if (!active) return;
        setErrorCode(''); failures = 0;
        handleStatus(result);
        if (isFinalReservationStatus(result)) { setPolling(false); return; }
        timer = setTimeout(poll, 2000);
      } catch (error) {
        if (active && !controller.signal.aborted) {
          const code = error instanceof BookingApiError ? error.code : 'NETWORK';
          setErrorCode(code);
          nextAllowed = Date.now() + Math.max(
            error instanceof BookingApiError ? error.retryAfterSeconds * 1000 : 0,
            Math.min(30_000, 2000 * 2 ** ++failures),
          );
          setRetryAt(nextAllowed);
          timer = setTimeout(poll, Math.min(nextAllowed - Date.now(), Math.max(0, deadline - Date.now())));
        }
      } finally {
        inFlight = false;
        if (active && controller.signal.aborted && !document.hidden) timer = setTimeout(poll, 2000);
      }
    };
    const visible = () => {
      clearTimeout(timer);
      if (document.hidden) { controller?.abort(); setPolling(false); }
      else void poll();
    };
    void poll();
    document.addEventListener('visibilitychange', visible);
    return () => { active = false; controller?.abort(); clearTimeout(timer); document.removeEventListener('visibilitychange', visible); };
    // Credential changes and explicit refresh start a bounded polling window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials, mismatch, recoveryBlocked, statusRevision, props.paymentReturnId]);

  const sendAttempt = async (value: ReservationAttempt) => {
    if (busy.current || Date.now() < retryAt) return;
    busy.current = true; setSubmitting(true); setErrorCode('');
    automaticRedirect.current = true;
    const controller = new AbortController(); createController.current = controller;
    try {
      const result = await createReservation(value.payload, value.key, controller.signal);
      if (!mounted.current) return;
      remember({ reservationId: result.reservationId, statusToken: result.statusToken });
      handleStatus(result);
    } catch (error) {
      if (!mounted.current) return;
      const apiError = error instanceof BookingApiError ? error : new BookingApiError('NETWORK');
      setErrorCode(apiError.code);
      setRetryAt(Date.now() + apiError.retryAfterSeconds * 1000);
      if (apiError.details.reservationId) {
        try { remember(parseCredentials(apiError.details)); }
        catch (failure) {
          setRecoveryBlocked(true);
          setErrorCode(failure instanceof BookingApiError ? failure.code : 'STORAGE_UNAVAILABLE');
        }
      } else if (!saved.current.credentials && (beforeWrite.has(apiError.code)
        || apiError.status === 400 || apiError.status === 422)) {
        try {
          clearReservationSession(window.sessionStorage);
          saved.current = { savedAt: 0 }; setAttempt(null); setAcceptedQuoteId('');
          if (apiError.code === 'TERMS_CHANGED') {
            updateConsents(previous => ({ ...previous, terms: false, privacy: false, deposit: false }));
            setConfirmedTermsVersion('');
          }
          props.onConflict(apiError.code);
        } catch { setErrorCode('STORAGE_UNAVAILABLE'); setRecoveryBlocked(true); }
      }
    } finally {
      busy.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  const customerErrors = validateCustomer(customer);
  const quoteAccepted = !!props.quote && acceptedQuoteId === props.quote.quoteId;
  const canSubmit = props.canContinue && !!props.selection && !!props.quote && !!props.termsVersion
    && quoteAccepted && !Object.keys(customerErrors).length && props.message.length <= 255
    && confirmedTermsVersion === props.termsVersion && consents.terms && consents.privacy && consents.deposit && !attempt && !credentials
    && !recoveryBlocked && !mismatch && !submitting && clock >= retryAt;
  const submit = async () => {
    if (!canSubmit || busy.current || saved.current.attempt || saved.current.credentials
      || !props.selection || !props.quote || !props.termsVersion
      || !isUsableQuote(props.quote, props.selection)) return;
    try {
      const { offer, ...selection } = props.selection;
      const value: ReservationAttempt = {
        key: crypto.randomUUID(), createdAt: Date.now(),
        payload: parseReservationPayload({
          ...selection, offerId: offer, accessoryIds: [...selection.accessoryIds].sort(), quoteId: props.quote.quoteId,
          customer: { name: customer.name.trim(), email: customer.email.trim(), phone: customer.phone.trim() },
          message: props.message, termsAccepted: consents.terms, rodoAccepted: consents.privacy,
          depositAccepted: consents.deposit, termsVersion: props.termsVersion, marketingConsent: consents.marketing, locale: props.locale,
        }),
      };
      persist({ savedAt: value.createdAt, attempt: value });
      setAttempt(value);
      await sendAttempt(value);
    } catch (error) { setErrorCode(error instanceof BookingApiError ? error.code : 'STORAGE_UNAVAILABLE'); }
  };
  const refresh = () => {
    if (Date.now() < retryAt) return;
    try {
      if (recoveryBlocked && !saved.current.credentials) {
        const session = readReservationSession(window.sessionStorage);
        saved.current = session ?? { savedAt: 0 };
        setCredentials(session?.credentials ?? null); setAttempt(session?.attempt ?? null);
        setRecoveryBlocked(!!session?.unresolved);
      } else { persist(saved.current); setRecoveryBlocked(false); }
      setErrorCode(''); setStatusRevision(value => value + 1);
    } catch (error) { setErrorCode(error instanceof BookingApiError ? error.code : 'STORAGE_UNAVAILABLE'); }
  };
  const leavePaymentReturn = async () => {
    if (props.paymentReturnId === undefined) return true;
    setLeaving(true);
    try {
      if (!props.onLeavePaymentReturn || !await props.onLeavePaymentReturn()) {
        setErrorCode('NAVIGATION_FAILED');
        return false;
      }
      return true;
    } catch {
      setErrorCode('NAVIGATION_FAILED');
      return false;
    } finally { setLeaving(false); }
  };
  const newReservation = async () => {
    if (mismatch || !canStartNewReservation(status) || !await leavePaymentReturn()) return false;
    try {
      clearReservationSession(window.sessionStorage);
      saved.current = { savedAt: 0 }; setAttempt(null); setCredentials(null); setStatus(null);
      setRecoveredPayload(null); setRecoveryBlocked(false); setRetryAt(0);
      setCustomer({ name: '', email: '', phone: '' });
      updateConsents({ terms: false, privacy: false, deposit: false, marketing: false });
      setConfirmedTermsVersion('');
      setErrorCode(''); setAcceptedQuoteId(''); automaticRedirect.current = false; redirected.current = false;
      props.onConflict('NEW_RESERVATION');
      return true;
    } catch { setErrorCode('STORAGE_UNAVAILABLE'); return false; }
  };
  const canRetryAttempt = !!attempt && !recoveryBlocked && !mismatch && (!credentials
    || !!status && status.paymentState === 'not-started' && ['pending', 'payment-pending'].includes(status.status)
      && Date.parse(status.holdExpiresAt) > clock);
  return {
    customer, setCustomer, customerErrors, consents, setConsents, quoteAccepted,
    setQuoteAccepted: (value: boolean) => setAcceptedQuoteId(value ? props.quote?.quoteId ?? '' : ''),
    attempt, credentials, status: mismatch ? null : status,
    errorCode: mismatch && errorCode !== 'NAVIGATION_FAILED' ? 'MISSING_STATUS_TOKEN' : errorCode,
    submitting, polling, leaving, sessionReady, canSubmit, submit, refresh, newReservation, recoveredPayload,
    canLeavePaymentReturn: mismatch, leavePaymentReturn,
    locked: !!attempt || !!credentials || recoveryBlocked || mismatch,
    recoveryBlocked: recoveryBlocked || mismatch, canRetryAttempt, waitingSeconds: Math.max(0, Math.ceil((retryAt - clock) / 1000)),
    retryAttempt: () => {
      if (attempt && canRetryAttempt && (!status || Date.parse(status.holdExpiresAt) > Date.now())) void sendAttempt(attempt);
    },
    resumePayment: () => {
      if (status && canOpenPayment(status)) {
        try { persist(saved.current); window.location.assign(status.redirectUri!); }
        catch { setErrorCode('STORAGE_UNAVAILABLE'); }
      }
    },
  };
}
